import { selectPipCoverage } from "@shared/coi-coverage";
import { EMPTY_AUTO_LIABILITY } from "@shared/coi-template";
import { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ChevronDown,
  ChevronRight,
  Download,
  Eye,
  Pencil,
  RefreshCw,
  Save,
  X,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { PdfViewer } from "@/components/PdfViewer";
import { StateSelect } from "@/components/state-select";
import { toIsoDate, fromIsoDate } from "@/lib/formatters";
import { apiFetch } from "@/lib/queryClient";
import type {
  CoiAutoLiabilityBlock,
  CoiGeneralLiabilityBlock,
  CoiInsurer,
  CoiOtherCoverageRow,
  CoiTemplateData,
  CoiUmbrellaBlock,
  CoiWorkersCompBlock,
} from "@shared/coi-template";

interface CoiTemplateEditorProps {
  open: boolean;
  onClose: () => void;
  policyId: string;
}

type Mode = "edit" | "preview";

const INSURER_LETTERS = "ABCDEF";

// The five ACORD 25 "Covered Autos" boxes, in form order. Independent booleans —
// multiple may be checked at once (CSR-1451). Labels match the ACORD 25 form.
type CoveredAutoKey =
  | "anyAuto"
  | "ownedAutos"
  | "scheduledAutos"
  | "hiredAutos"
  | "nonOwnedAutos";
const COVERED_AUTO_OPTIONS: { key: CoveredAutoKey; label: string }[] = [
  { key: "anyAuto", label: "Any Auto" },
  { key: "ownedAutos", label: "Owned Autos Only" },
  { key: "scheduledAutos", label: "Scheduled Autos" },
  { key: "hiredAutos", label: "Hired Autos Only" },
  { key: "nonOwnedAutos", label: "Non-Owned Autos Only" },
];

function Section({
  title,
  open,
  onToggle,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="border border-border/50 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-4 py-2.5 bg-muted/30 hover:bg-muted/50 text-left text-sm font-semibold transition-colors cursor-pointer"
      >
        {open ? (
          <ChevronDown className="w-4 h-4" />
        ) : (
          <ChevronRight className="w-4 h-4" />
        )}
        {title}
      </button>
      {open && <div className="px-4 py-3 space-y-3">{children}</div>}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  testId,
  type,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  testId?: string;
  type?: "text" | "state" | "date";
}) {
  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground">
        {label}
      </label>
      {type === "state" ? (
        <StateSelect
          value={value}
          onChange={onChange}
          className="!mt-0.5 h-8 text-sm py-1"
        />
      ) : type === "date" ? (
        <Input
          type="date"
          className="mt-0.5 h-8 text-sm"
          value={toIsoDate(value)}
          onChange={(e) => onChange(fromIsoDate(e.target.value))}
          data-testid={testId}
        />
      ) : (
        <Input
          className="mt-0.5 h-8 text-sm"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testId}
        />
      )}
    </div>
  );
}

// Read-only variant of Field. Used for Physical Damage's Limit/Deductible: PD's
// deductible is a policy-derived Comp/Coll composite (carried in
// `row.limitDisplay`) that a single scalar input can't represent, and the PDF
// renderer always renders the composite — so an editable field would silently
// discard any typed value. We show the composite as read-only instead.
function ReadOnlyField({
  label,
  value,
  testId,
}: {
  label: string;
  value: string;
  testId?: string;
}) {
  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground">
        {label}
      </label>
      <Input
        className="mt-0.5 h-8 text-sm bg-muted/40 text-muted-foreground cursor-not-allowed"
        value={value}
        readOnly
        aria-readonly="true"
        tabIndex={-1}
        data-testid={testId}
      />
    </div>
  );
}

export function CoiTemplateEditor({
  open,
  onClose,
  policyId,
}: CoiTemplateEditorProps) {
  const { toast } = useToast();
  const [data, setData] = useState<CoiTemplateData | null>(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<Mode>("edit");
  const [pdfBytes, setPdfBytes] = useState<Uint8Array | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSavedDialog, setShowSavedDialog] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    producer: true,
    insured: true,
    insurers: false,
    gl: false,
    auto: false,
    umbrella: false,
    wc: false,
    other: false,
    remarks: false,
  });

  const pipCoverage = data?.autoLiability ? selectPipCoverage(data.autoLiability) : undefined;

  const toggle = (key: string) =>
    setExpanded((p) => ({ ...p, [key]: !p[key] }));

  useEffect(() => {
    if (!open || !policyId) return;
    let cancelled = false;
    setLoading(true);
    setPdfBytes(null);
    setPdfUrl(null);
    setMode("edit");
    apiFetch(`/api/policies/${policyId}/coi-template-data`)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load template (${res.status})`);
        return res.json();
      })
      .then((seed: CoiTemplateData) => {
        if (cancelled) return;
        setData(
          seed.autoLiability
            ? seed
            : { ...seed, autoLiability: EMPTY_AUTO_LIABILITY },
        );
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        toast({
          title: "Failed to load COI template",
          description: err instanceof Error ? err.message : String(err),
          variant: "destructive",
        });
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, policyId, toast]);

  // Revoke object URLs on change/unmount to avoid leaks.
  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const generatePreview = useCallback(async () => {
    if (!data) return;
    setGeneratingPdf(true);
    setPdfBytes(null);
    setPdfUrl(null);
    try {
      const res = await apiFetch("/api/coi/preview", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Preview failed (${res.status})`);
      }
      const buf = await res.arrayBuffer();
      const bytes = new Uint8Array(buf);
      // Build the download Blob BEFORE setPdfBytes triggers PdfViewer's
      // pdfjs render (which transfers and detaches the underlying buffer).
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setPdfBytes(bytes);
      setMode("preview");
    } catch (err) {
      toast({
        title: "Failed to generate preview",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setGeneratingPdf(false);
    }
  }, [data, toast]);

  // CSR-1117: persist the current template edits per insured (resolved
  // server-side from policyId). On success surface the confirmation dialog.
  const saveTemplate = useCallback(async () => {
    if (!data) return;
    setSaving(true);
    try {
      const res = await apiFetch(`/api/policies/${policyId}/coi-template`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Save failed (${res.status})`);
      }
      setShowSavedDialog(true);
    } catch (err) {
      toast({
        title: "Failed to save COI template",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }, [data, policyId, toast]);

  const handleDownload = () => {
    if (!pdfUrl || !data) return;
    const a = document.createElement("a");
    const name = data.certificateHolder.name?.trim() || "preview";
    a.href = pdfUrl;
    a.download = `COI_${name.replace(/\s+/g, "_")}.pdf`;
    a.click();
  };

  // Generic per-field updater for top-level string fields.
  const setField = <K extends keyof CoiTemplateData>(
    key: K,
    value: CoiTemplateData[K],
  ) => setData((prev) => (prev ? { ...prev, [key]: value } : prev));

  const setInsurer = (i: number, key: keyof CoiInsurer, value: string) =>
    setData((prev) => {
      if (!prev) return prev;
      const next = prev.insurers.slice();
      next[i] = { ...next[i], [key]: value };
      return { ...prev, insurers: next };
    });

  const setGL = <K extends keyof CoiGeneralLiabilityBlock>(
    key: K,
    value: CoiGeneralLiabilityBlock[K],
  ) =>
    setData((prev) =>
      prev
        ? {
            ...prev,
            generalLiability: { ...prev.generalLiability, [key]: value },
          }
        : prev,
    );

  const setAuto = <K extends keyof CoiAutoLiabilityBlock>(
    key: K,
    value: CoiAutoLiabilityBlock[K],
  ) =>
    setData((prev) =>
      prev?.autoLiability
        ? { ...prev, autoLiability: { ...prev.autoLiability, [key]: value } }
        : prev,
    );

  const setUmbrella = <K extends keyof CoiUmbrellaBlock>(
    key: K,
    value: CoiUmbrellaBlock[K],
  ) =>
    setData((prev) =>
      prev
        ? {
            ...prev,
            umbrellaLiability: { ...prev.umbrellaLiability, [key]: value },
          }
        : prev,
    );

  const setWC = <K extends keyof CoiWorkersCompBlock>(
    key: K,
    value: CoiWorkersCompBlock[K],
  ) =>
    setData((prev) =>
      prev
        ? { ...prev, workersComp: { ...prev.workersComp, [key]: value } }
        : prev,
    );

  const setOtherRow = (
    i: number,
    key: keyof CoiOtherCoverageRow,
    value: string,
  ) =>
    setData((prev) => {
      if (!prev?.otherCoverages) return prev;
      const next = prev.otherCoverages.slice();
      next[i] = { ...next[i], [key]: value };
      return { ...prev, otherCoverages: next };
    });

  const addOtherRow = () =>
    setData((prev) => {
      if (!prev) return prev;
      const row: CoiOtherCoverageRow = {
        insurerLetter: "A",
        description: "",
        policyNumber: "",
        effectiveDate: "",
        expirationDate: "",
        limit: "",
        deductible: "",
      };
      return { ...prev, otherCoverages: [...(prev.otherCoverages ?? []), row] };
    });

  const removeOtherRow = (i: number) =>
    setData((prev) => {
      if (!prev?.otherCoverages) return prev;
      const next = prev.otherCoverages.slice();
      next.splice(i, 1);
      return { ...prev, otherCoverages: next };
    });

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[1100px] max-h-[90vh] p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-border/50">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-lg">
                COI — Certificate of Liability Insurance
              </DialogTitle>
              <DialogDescription>
                {mode === "edit"
                  ? "Edit template fields, then generate a preview"
                  : "PDF preview — download or go back to edit"}
              </DialogDescription>
            </div>
            <div className="flex gap-2">
              {mode === "preview" && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 rounded-lg"
                  onClick={() => setMode("edit")}
                  data-testid="coi-editor-edit"
                >
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </Button>
              )}
              {mode === "preview" && pdfUrl && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 rounded-lg"
                  onClick={handleDownload}
                  data-testid="coi-editor-download"
                >
                  <Download className="w-3.5 h-3.5" /> Download
                </Button>
              )}
              {mode === "edit" && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 rounded-lg"
                  disabled={!data || saving}
                  onClick={saveTemplate}
                  data-testid="coi-editor-save"
                >
                  <Save className="w-3.5 h-3.5" />
                  {saving ? "Saving..." : "Save"}
                </Button>
              )}
              {mode === "edit" && (
                <Button
                  size="sm"
                  className="gap-1.5 rounded-lg"
                  disabled={!data || generatingPdf}
                  onClick={generatePreview}
                  data-testid="coi-editor-generate-preview"
                >
                  <Eye className="w-3.5 h-3.5" />
                  {generatingPdf ? "Generating..." : "Generate Preview"}
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center h-[500px]">
            <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : mode === "preview" && pdfBytes ? (
          <div className="h-[calc(90vh-120px)]">
            <PdfViewer data={pdfBytes} />
          </div>
        ) : mode === "edit" && data ? (
          <div
            className="overflow-y-auto px-6 py-4 space-y-3"
            style={{ maxHeight: "calc(90vh - 120px)" }}
          >
            <Section
              title="Producer / Agency"
              open={expanded.producer}
              onToggle={() => toggle("producer")}
            >
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Producer Name"
                  value={data.producerName}
                  onChange={(v) => setField("producerName", v)}
                />
                <Field
                  label="Contact Person"
                  value={data.producerContactName}
                  onChange={(v) => setField("producerContactName", v)}
                />
              </div>
              <Field
                label="Address"
                value={data.producerAddress}
                onChange={(v) => setField("producerAddress", v)}
              />
              <div className="grid grid-cols-3 gap-3">
                <Field
                  label="City"
                  value={data.producerCity}
                  onChange={(v) => setField("producerCity", v)}
                />
                <Field
                  label="State"
                  type="state"
                  value={data.producerState}
                  onChange={(v) => setField("producerState", v)}
                />
                <Field
                  label="Zip"
                  value={data.producerZip}
                  onChange={(v) => setField("producerZip", v)}
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Field
                  label="Phone"
                  value={data.producerPhone}
                  onChange={(v) => setField("producerPhone", v)}
                />
                <Field
                  label="Fax"
                  value={data.producerFax}
                  onChange={(v) => setField("producerFax", v)}
                />
                <Field
                  label="Email"
                  value={data.producerEmail}
                  onChange={(v) => setField("producerEmail", v)}
                />
              </div>
            </Section>

            <Section
              title="Named Insured"
              open={expanded.insured}
              onToggle={() => toggle("insured")}
            >
              <Field
                label="Insured Name"
                value={data.insuredName}
                onChange={(v) => setField("insuredName", v)}
              />
              <Field
                label="Address"
                value={data.insuredAddress}
                onChange={(v) => setField("insuredAddress", v)}
              />
              <div className="grid grid-cols-3 gap-3">
                <Field
                  label="City"
                  value={data.insuredCity}
                  onChange={(v) => setField("insuredCity", v)}
                />
                <Field
                  label="State"
                  type="state"
                  value={data.insuredState}
                  onChange={(v) => setField("insuredState", v)}
                />
                <Field
                  label="Zip"
                  value={data.insuredZip}
                  onChange={(v) => setField("insuredZip", v)}
                />
              </div>
            </Section>

            <Section
              title={`Insurers (${data.insurers.length})`}
              open={expanded.insurers}
              onToggle={() => toggle("insurers")}
            >
              {data.insurers.map((ins, i) => (
                <div
                  key={i}
                  className="grid grid-cols-[auto_1fr_1fr] gap-3 items-end"
                >
                  <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center text-primary text-xs font-bold">
                    {INSURER_LETTERS[i] ?? "?"}
                  </div>
                  <Field
                    label="Name"
                    value={ins.name}
                    onChange={(v) => setInsurer(i, "name", v)}
                  />
                  <Field
                    label="NAIC Code"
                    value={ins.naic}
                    onChange={(v) => setInsurer(i, "naic", v)}
                  />
                </div>
              ))}
            </Section>

            <Section
              title="General Liability"
              open={expanded.gl}
              onToggle={() => toggle("gl")}
            >
              <div className="grid grid-cols-3 gap-3">
                <Field
                  label="Insurer Letter"
                  value={data.generalLiability.insurerLetter}
                  onChange={(v) => setGL("insurerLetter", v)}
                />
                <Field
                  label="Policy Number"
                  value={data.generalLiability.policyNumber}
                  onChange={(v) => setGL("policyNumber", v)}
                />
                <div />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Effective Date"
                  type="date"
                  value={data.generalLiability.effectiveDate}
                  onChange={(v) => setGL("effectiveDate", v)}
                />
                <Field
                  label="Expiration Date"
                  type="date"
                  value={data.generalLiability.expirationDate}
                  onChange={(v) => setGL("expirationDate", v)}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Each Occurrence"
                  value={data.generalLiability.eachOccurrence}
                  onChange={(v) => setGL("eachOccurrence", v)}
                />
                <Field
                  label="Damage to Rented Premises"
                  value={data.generalLiability.fireDamage}
                  onChange={(v) => setGL("fireDamage", v)}
                />
                <Field
                  label="Med Exp"
                  value={data.generalLiability.medExp}
                  onChange={(v) => setGL("medExp", v)}
                />
                <Field
                  label="Personal & Adv Injury"
                  value={data.generalLiability.personalInjury}
                  onChange={(v) => setGL("personalInjury", v)}
                />
                <Field
                  label="General Aggregate"
                  value={data.generalLiability.generalAggregate}
                  onChange={(v) => setGL("generalAggregate", v)}
                />
                <Field
                  label="Products / Comp-Op Agg"
                  value={data.generalLiability.productsCompOp}
                  onChange={(v) => setGL("productsCompOp", v)}
                />
              </div>
            </Section>

            {/* CSR-1451: always rendered (even with no auto coverage in AMS) so
                the team can fill in the Covered Autos boxes manually. */}
            <Section
              title="Automobile Liability"
              open={expanded.auto}
              onToggle={() => toggle("auto")}
            >
              <div className="grid grid-cols-3 gap-3">
                <Field
                  label="Insurer Letter"
                  value={data.autoLiability.insurerLetter}
                  onChange={(v) => setAuto("insurerLetter", v)}
                />
                <Field
                  label="Policy Number"
                  value={data.autoLiability.policyNumber}
                  onChange={(v) => setAuto("policyNumber", v)}
                />
                <div />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Effective Date"
                  type="date"
                  value={data.autoLiability.effectiveDate}
                  onChange={(v) => setAuto("effectiveDate", v)}
                />
                <Field
                  label="Expiration Date"
                  type="date"
                  value={data.autoLiability.expirationDate}
                  onChange={(v) => setAuto("expirationDate", v)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground">
                  Covered Autos
                </label>
                <div className="grid grid-cols-3 gap-x-4 gap-y-2">
                  {COVERED_AUTO_OPTIONS.map(({ key, label }) => (
                    <label
                      key={key}
                      className="flex items-center gap-2 cursor-pointer text-sm"
                    >
                      <Checkbox
                        checked={data.autoLiability[key]}
                        onCheckedChange={(checked) => setAuto(key, checked === true)}
                        data-testid={`coi-auto-${key}`}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Combined Single Limit"
                  value={data.autoLiability.combinedSingleLimit}
                  onChange={(v) => setAuto("combinedSingleLimit", v)}
                />
                <Field
                  label="Bodily Injury / Person"
                  value={data.autoLiability.bodilyInjuryPerson}
                  onChange={(v) => setAuto("bodilyInjuryPerson", v)}
                />
                <Field
                  label="Bodily Injury / Accident"
                  value={data.autoLiability.bodilyInjuryAccident}
                  onChange={(v) => setAuto("bodilyInjuryAccident", v)}
                />
                <Field
                  label="Property Damage"
                  value={data.autoLiability.propertyDamage}
                  onChange={(v) => setAuto("propertyDamage", v)}
                />
                <div>
                  <label htmlFor="coi-policy-pip" className="text-xs font-medium text-muted-foreground">
                    {pipCoverage?.label ?? "PIP"} (from policy)
                  </label>
                  <Input
                    id="coi-policy-pip"
                    className="mt-0.5 h-8 text-sm"
                    value={pipCoverage?.limit ?? ""}
                    readOnly
                  />
                </div>
                <Field
                  label="Non-Trucking Liability Limit"
                  value={data.autoLiability.nonTruckingLiabilityLimit ?? ""}
                  onChange={(v) => setAuto("nonTruckingLiabilityLimit", v)}
                />
              </div>
            </Section>

            <Section
              title="Umbrella / Excess Liability"
              open={expanded.umbrella}
              onToggle={() => toggle("umbrella")}
            >
                <div className="grid grid-cols-3 gap-3">
                  <Field
                    label="Insurer Letter"
                    value={data.umbrellaLiability.insurerLetter}
                    onChange={(v) => setUmbrella("insurerLetter", v)}
                  />
                  <Field
                    label="Policy Number"
                    value={data.umbrellaLiability.policyNumber}
                    onChange={(v) => setUmbrella("policyNumber", v)}
                  />
                  <div />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field
                    label="Effective Date"
                    type="date"
                    value={data.umbrellaLiability.effectiveDate}
                    onChange={(v) => setUmbrella("effectiveDate", v)}
                  />
                  <Field
                    label="Expiration Date"
                    type="date"
                    value={data.umbrellaLiability.expirationDate}
                    onChange={(v) => setUmbrella("expirationDate", v)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field
                    label="Each Occurrence"
                    value={data.umbrellaLiability.eachOccurrence}
                    onChange={(v) => setUmbrella("eachOccurrence", v)}
                  />
                  <Field
                    label="Aggregate"
                    value={data.umbrellaLiability.aggregate}
                    onChange={(v) => setUmbrella("aggregate", v)}
                  />
                </div>
              </Section>

            <Section
              title="Workers Compensation"
              open={expanded.wc}
              onToggle={() => toggle("wc")}
            >
                <div className="grid grid-cols-3 gap-3">
                  <Field
                    label="Insurer Letter"
                    value={data.workersComp.insurerLetter}
                    onChange={(v) => setWC("insurerLetter", v)}
                  />
                  <Field
                    label="Policy Number"
                    value={data.workersComp.policyNumber}
                    onChange={(v) => setWC("policyNumber", v)}
                  />
                  <div />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field
                    label="Effective Date"
                    type="date"
                    value={data.workersComp.effectiveDate}
                    onChange={(v) => setWC("effectiveDate", v)}
                  />
                  <Field
                    label="Expiration Date"
                    type="date"
                    value={data.workersComp.expirationDate}
                    onChange={(v) => setWC("expirationDate", v)}
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Field
                    label="Each Accident"
                    value={data.workersComp.eachAccident}
                    onChange={(v) => setWC("eachAccident", v)}
                  />
                  <Field
                    label="Disease / Employee"
                    value={data.workersComp.diseaseEmployee}
                    onChange={(v) => setWC("diseaseEmployee", v)}
                  />
                  <Field
                    label="Disease / Policy Limit"
                    value={data.workersComp.diseasePolicyLimit}
                    onChange={(v) => setWC("diseasePolicyLimit", v)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <Checkbox
                      checked={data.workersComp.perStatute}
                      onCheckedChange={(checked) =>
                        setWC("perStatute", checked === true)
                      }
                      data-testid="coi-wc-per-statute"
                    />
                    Per Statute {data.workersComp.perStatute ? "" : "(Other)"}
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <Checkbox
                      checked={data.workersComp.proprietorExcluded === "Y"}
                      onCheckedChange={(checked) =>
                        setWC("proprietorExcluded", checked === true ? "Y" : "N")
                      }
                      data-testid="coi-wc-proprietor-excluded"
                    />
                    Proprietor / Partner / Officer Excluded
                  </label>
                </div>
              </Section>

            <Section
              title={`Other Coverages (${data.otherCoverages?.length ?? 0})`}
              open={expanded.other}
              onToggle={() => toggle("other")}
            >
              {(data.otherCoverages ?? []).map((row, i) => (
                <div
                  key={i}
                  className="rounded-md border border-border/40 bg-muted/20 p-3 space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground">
                      Coverage Row {i + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeOtherRow(i)}
                      className="text-muted-foreground hover:text-destructive p-1 rounded cursor-pointer"
                      aria-label="Remove row"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-[80px_1fr_1fr] gap-3">
                    <Field
                      label="Insurer"
                      value={row.insurerLetter}
                      onChange={(v) => setOtherRow(i, "insurerLetter", v)}
                    />
                    <Field
                      label="Coverage / Description"
                      value={row.description}
                      onChange={(v) => setOtherRow(i, "description", v)}
                    />
                    <Field
                      label="Policy Number"
                      value={row.policyNumber}
                      onChange={(v) => setOtherRow(i, "policyNumber", v)}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field
                      label="Effective Date"
                      type="date"
                      value={row.effectiveDate}
                      onChange={(v) => setOtherRow(i, "effectiveDate", v)}
                    />
                    <Field
                      label="Expiration Date"
                      type="date"
                      value={row.expirationDate}
                      onChange={(v) => setOtherRow(i, "expirationDate", v)}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {row.limitDisplay ? (
                      <>
                        <ReadOnlyField
                          label="Limit"
                          value={row.limit || "—"}
                          testId={`coi-other-limit-${i}`}
                        />
                        <ReadOnlyField
                          label="Deductible"
                          value={row.limitDisplay}
                          testId={`coi-other-deductible-${i}`}
                        />
                      </>
                    ) : (
                      <>
                        <Field
                          label="Limit"
                          value={row.limit}
                          onChange={(v) => setOtherRow(i, "limit", v)}
                          testId={`coi-other-limit-${i}`}
                        />
                        <Field
                          label="Deductible"
                          value={row.deductible}
                          onChange={(v) => setOtherRow(i, "deductible", v)}
                          testId={`coi-other-deductible-${i}`}
                        />
                      </>
                    )}
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="w-full mt-2 py-2 text-xs font-medium text-muted-foreground hover:text-foreground border border-dashed border-border/50 rounded-md hover:bg-muted/30 transition-colors cursor-pointer"
                onClick={addOtherRow}
              >
                + Add Coverage Row
              </button>
            </Section>

            <Section
              title="Description of Operations / Remarks"
              open={expanded.remarks}
              onToggle={() => toggle("remarks")}
            >
              <Textarea
                rows={3}
                value={data.descriptionOfOperations}
                onChange={(e) =>
                  setField("descriptionOfOperations", e.target.value)
                }
                placeholder="Description of operations, locations, vehicles, etc."
                data-testid="coi-editor-description"
              />
            </Section>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
    <AlertDialog open={showSavedDialog} onOpenChange={setShowSavedDialog}>
      <AlertDialogContent data-testid="coi-saved-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>COI template saved.</AlertDialogTitle>
          <AlertDialogDescription>
            Warning: changes will be overwritten on the next policy sync.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction
            onClick={() => setShowSavedDialog(false)}
            data-testid="coi-saved-dialog-ok"
          >
            OK
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
